import React from "react";

import { Clock, RotateCcw, UploadCloud, ArrowRight, ChevronRight, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import Skeleton from "@/components/shared/Skeleton";
import { cn } from "@/lib/utils";

// "Needs Attention" worklist (Phase 2A, DL-Q2/Q6): three prioritised categories,
// a short preview each + a "View More" link into a pre-filtered Document Intake
// (DL-Q5). Each category is fed a `{ items, total, intakeStatusFilter }` block
// from GET /dashboard/attention. Rows click through to the candidate review
// screen; "View More" deep-links to Intake with ?status= applied:
//   Pending Review  → ?status=pending_for_review
//   Stuck           → ?status=re_requested
//   Never Uploaded  → ?status=pending_from_candidate
// (the status values come from the backend as `intakeStatusFilter`).
//
// The endpoint returns one item per *candidate*, with that candidate's document
// names already collapsed into `item.documents` and `total` counting candidates
// — so there is no grouping to do here and `total` can decide "View More" on
// its own.

// Candidate rows to preview per category before the "View More" link.
const PREVIEW_LIMITS = {
  pendingReview: 2,
  stuck: 3,
  neverUploaded: 3,
};

// Document pills rendered inline before the "+N" overflow badge.
const PILL_LIMIT = 2;

// Per-category presentation (icon + accent). Keyed to the AttentionResponse keys.
const CATEGORY_META = {
  pendingReview: {
    title: "Pending Review",
    subtitle: "Candidates with documents awaiting review",
    icon: Clock,
    color: "#3b82f6",
  },
  stuck: {
    title: "Stuck Candidates",
    subtitle: "Re-requested, no response yet",
    icon: RotateCcw,
    color: "#f59e0b",
  },
  neverUploaded: {
    title: "Never Uploaded",
    subtitle: "Sent to compliance, nothing received",
    icon: UploadCloud,
    color: "#a855f7",
  },
};

// Age badge tint scales with urgency: fresh (gray) → aging (amber) → old (red).
const ageBadgeClasses = (days) => {
  if (days >= 7) return "bg-tone-rose-soft text-tone-rose border-tone-rose/30";
  if (days >= 3) return "bg-tone-amber-soft text-tone-amber border-tone-amber/30";
  return "bg-background-tertiary text-text-secondary border-border-primary";
};

const ageLabel = (days) => {
  if (!days || days <= 0) return "Today";
  if (days === 1) return "1 day";
  return `${days} days`;
};

const AttentionCandidateRow = ({ group, color, onClick }) => {
  // Only the pending-review list carries documents; the other two are
  // candidate-level and send an empty list.
  const documents = group.documents ?? [];
  const visibleDocs = documents.slice(0, PILL_LIMIT);
  const hiddenCount = documents.length - visibleDocs.length;

  return (
    <Button
      type="button"
      onClick={onClick}
      className="w-full flex flex-col items-stretch gap-1 py-2 px-1 border-b border-border-primary last:border-b-0 text-left hover:bg-background-tertiary rounded-md transition-colors group"
    >
      <div className="flex items-center gap-2.5 w-full">
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-text-primary leading-snug truncate font-medium">{group.candidateName || "—"}</p>
          <p className="text-[10px] text-text-secondary truncate">{group.candidateEmail || "—"}</p>
        </div>
        <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full border whitespace-nowrap", ageBadgeClasses(group.ageDays))}>{ageLabel(group.ageDays)}</span>
        <ChevronRight size={13} className="text-text-secondary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
      </div>

      {/* Document pills — one line, the overflow badge stays on it */}
      {documents.length > 0 && (
        <div className="flex items-center gap-1.5 flex-nowrap overflow-hidden pl-4">
          {visibleDocs.map((docName) => (
            <span
              key={docName}
              title={docName}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background-form/60 border border-border-primary/60 text-[10px] text-text-primary/70 min-w-0"
            >
              <FileText size={9} className="shrink-0" />
              <span className="truncate">{docName}</span>
            </span>
          ))}
          {hiddenCount > 0 && (
            <span className="shrink-0 px-2 py-0.5 rounded-md bg-button-primary/10 text-button-primary text-[10px] font-bold whitespace-nowrap">
              +{hiddenCount} {hiddenCount === 1 ? "Document" : "Documents"}
            </span>
          )}
        </div>
      )}
    </Button>
  );
};

const CategoryColumn = ({ categoryKey, block, loading, navigate }) => {
  const meta = CATEGORY_META[categoryKey];
  const Icon = meta.icon;
  const total = block?.total ?? 0;
  const previewLimit = PREVIEW_LIMITS[categoryKey];

  // Already one row per candidate, aged off their oldest pending document.
  const visibleGroups = (block?.items ?? []).slice(0, previewLimit);

  // `total` counts candidates, the same unit as the rows, so this is simply
  // "are there candidates we are not showing".
  const hasMoreCandidates = total > previewLimit;

  const goToIntake = () => {
    const status = block?.intakeStatusFilter;
    navigate(status ? `/admin/compliance/intake?status=${status}` : "/admin/compliance/intake");
  };

  return (
    <div className="flex flex-col min-w-0 h-full">
      {/* Category header */}
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 border"
          style={{ background: `${meta.color}1a`, borderColor: `${meta.color}33` }}
        >
          <Icon size={14} style={{ color: meta.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h4 className="text-xs font-semibold text-text-primary leading-none">{meta.title}</h4>
            {!loading && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: `${meta.color}1a`, color: meta.color }}>
                {total}
              </span>
            )}
          </div>
          <p className="text-[10px] text-text-secondary mt-0.5 truncate">{meta.subtitle}</p>
        </div>
      </div>

      {/* Rows — a fixed min-height keeps the three columns aligned even
                when one has fewer items or is empty. */}
      <div className="flex-1 min-h-[132px] overflow-hidden">
        {loading ? (
          <div className="flex flex-col gap-2 py-1">
            {[...Array(previewLimit)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : visibleGroups.length === 0 ? (
          <div className="flex items-center justify-center h-full py-6">
            <p className="text-[11px] text-text-secondary text-center">Nothing here — all clear.</p>
          </div>
        ) : (
          visibleGroups.map((group) => (
            <AttentionCandidateRow
              key={`${categoryKey}-${group.complianceCandidateId || group.candidateName}`}
              group={group}
              color={meta.color}
              onClick={() => navigate(`/admin/compliance/${group.complianceCandidateId}`)}
            />
          ))
        )}
      </div>

      {/* View More — only when there is genuinely more than the preview shows */}
      {!loading && hasMoreCandidates && (
        <Button
          type="button"
          onClick={goToIntake}
          className="mt-2 flex items-center justify-center gap-1 text-[11px] font-medium py-1.5 rounded-md border border-border-primary hover:bg-background-tertiary/60 transition-colors"
          style={{ color: meta.color }}
        >
          View More
          <ArrowRight size={11} />
        </Button>
      )}
    </div>
  );
};

const NeedsAttentionPanel = ({ data, loading }) => {
  const navigate = useNavigate();

  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-4 py-3 border-b border-border-primary flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Clock size={14} className="text-text-primary/70" />
          <h3 className="text-sm font-semibold text-text-primary">Needs Attention</h3>
        </div>
        {/* {!loading && typeof data?.stuckThresholdDays === "number" && (
                    <span className="text-[10px] text-text-secondary">
                        “Stuck” = no response in {data.stuckThresholdDays}+ days
                    </span>
                )} */}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 p-4 gap-4 md:gap-0">
        {["pendingReview", "stuck", "neverUploaded"].map((key, i) => (
          <div key={key} className={cn("md:px-4 first:md:pl-0 last:md:pr-0", i > 0 && "border-t md:border-t-0 md:border-l border-border-primary pt-4 md:pt-0")}>
            <CategoryColumn categoryKey={key} block={data?.[key]} loading={loading} navigate={navigate} />
          </div>
        ))}
      </div>
    </Card>
  );
};

export default NeedsAttentionPanel;
